import { useMemo, useState, useLayoutEffect, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import useAppStore from "../../store/useAppStore"
import useLayoutStore from "../../store/useLayoutStore"

import ViewDevicesHeader from "../elements/ViewDevicesHeader"
import DevicesCard from "../elements/DevicesCard";
import LoadingSpinner from "../elements/LoadingSpinner";

const DeviceUpdateSuccessToast = () => {
    const { t } = useTranslation("devices");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const setDeviceUpdateSuccessToast = useAppStore(state => state.setDeviceUpdateSuccessToast);

    const dismiss = () => setDeviceUpdateSuccessToast(false);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, []);

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                {t("toast.saved")}
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const DevicesView = () => {
    const devicesData = useAppStore(state => state.devicesData)
    const deviceDataSignal = useAppStore(state => state.deviceDataSignal)
    const deviceUpdateSuccessToast = useAppStore(state => state.deviceUpdateSuccessToast)
    const setDevicesData = useAppStore(state => state.setDevicesData)
    const setDevicesNewStatus = useAppStore(state => state.setDevicesNewStatus)

    useLayoutEffect(() => {
        if (!devicesData) {
            setDevicesData();
        }
    }, [])

    useEffect(() => {
        if (!deviceDataSignal) return;

        // Zajednička funkcija za obradu uređaja
        const processDevices = (deviceTypes) => {
            const currentState = useAppStore.getState().devicesState;

            const waitingDevices = [];
            const onlineDevices = [];

            Object.keys(currentState).forEach(key => {
                const device = currentState[key];

                if (deviceTypes.includes(device.deviceType)) {
                    if (device.status === 'waiting') {
                        waitingDevices.push(key);
                    } else if (device.status === 'online') {
                        onlineDevices.push(key);
                    }
                }
            });

            setDevicesNewStatus({ waitingDevices, onlineDevices });
        };

        // Prvi interval - svakih 24 sekundi za TX1, TX2, S26R2
        const interval24s = setInterval(() => {
            processDevices(['TX1', 'TX2', 'S26R2']);
        }, 24000);

        // Drugi interval - svakih 44 sekunde za PORTIR, Door Lock
        const interval44s = setInterval(() => {
            processDevices(['PORTIR', 'Door Lock']);
        }, 44000);

        return () => {
            clearInterval(interval24s);
            clearInterval(interval44s);
        };

    }, [deviceDataSignal]);

    const devicesByGroup = useMemo(() => {
        if (!devicesData) return 'loading'

        const grouped = {};

        Object.values(devicesData).forEach(device => {
            const group = device.group || 'Bez grupe';
            if (!grouped[group]) {
                grouped[group] = [];
            }
            grouped[group].push(device);
        });

        return grouped;

    }, [devicesData])

    const hasShownDevicesOnceRef = useRef(false);

    useEffect(() => {
        if (devicesByGroup !== "loading" && !hasShownDevicesOnceRef.current) {
            hasShownDevicesOnceRef.current = true;
        }
    }, [devicesByGroup]);

    return (
        <div className="view-container">
            <ViewDevicesHeader />
            <AnimatePresence>
                {deviceUpdateSuccessToast && <DeviceUpdateSuccessToast key="device-toast" />}
            </AnimatePresence>
            <AnimatePresence mode="wait">
                {devicesByGroup === 'loading' ? (
                    <LoadingSpinner />
                ) : (
                    <motion.div
                        key="content"
                        initial={{ opacity: 0, x: 14 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 14 }}
                        transition={{
                            duration: 0.24,
                            ease: "easeInOut",
                            delay: hasShownDevicesOnceRef.current ? 0 : 0.14
                        }}
                        className="view-groups-card-container"
                    >
                        {Object.entries(devicesByGroup)
                            .sort(([groupA], [groupB]) => {
                                if (groupA === "Bez grupe") return 1;
                                if (groupB === "Bez grupe") return -1;
                                return groupA.localeCompare(groupB, "sr", { sensitivity: "base" });
                            })
                            .map(([groupName, groupDevices]) => (
                                <DevicesGroupSection key={groupName} groupName={groupName} devices={groupDevices} />
                            ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default DevicesView;

// Prati .view-group-name visinu po bigScreenTier-u (style.css / bigScreen.css)
const COLLAPSED_GROUP_HEIGHTS = [49, 56, 66]

const DevicesGroupSection = ({ groupName, devices }) => {
    const { t } = useTranslation("devices");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const bigScreenTier = useLayoutStore(state => state.bigScreenTier)

    const cardsInRow = useLayoutStore(state => state.devicesCardsInRow)
    const groupState = useAppStore(state => state.deviceGroupsState[groupName])

    const [isVisible, setIsVisible] = useState(true)

    const emptySlots = Math.max(0, cardsInRow - groupState.visibleCards)

    useLayoutEffect(() => {
        if (groupState.visibleCards === 0) {
            setIsVisible(false)
        } else {
            setIsVisible(true)
        }
    }, [groupState.visibleCards])

    return (
        <motion.div
            className={`view-group-section  dv-group-section ${isDarkTheme ? 'dark-group-section' : 'white-group-section'}`}
            animate={{ height: isVisible ? "auto" : COLLAPSED_GROUP_HEIGHTS[bigScreenTier] }}
            transition={{ duration: 0.24, ease: "easeInOut" }}
        >
            <div
                className={`view-group-name ${isDarkTheme ? 'dark-group-name' : 'white-group-name'}`}
                onClick={() => setIsVisible(!isVisible)}
            >
                {groupName === "Bez grupe" ? t("filters.noGroup") : groupName}
                <div className="pv-group-button-container">
                    <motion.div
                        className="pv-group-button"
                        animate={{ rotate: isVisible ? 0 : 180, color: isVisible ? "#3498db" : "#6d6d6d" }}
                        transition={{ duration: 0.19, ease: "easeInOut" }}
                        style={{ transformOrigin: "center" }}
                    >
                        <svg
                            // xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="40"
                            height="40"
                            style={{ display: "block" }}
                        >
                            <path
                                d="M7 10l5 5 5-5"
                                fill="none"
                                stroke={isDarkTheme ? "#e2e1e1ff" : "black"}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </motion.div>
                </div>
            </div>
            <div className="view-card-grid dv-card-grid">
                {devices.map((device, index) => (
                    <DevicesCard key={device.client} device={device} index={index} />
                ))}
                {Array.from({ length: emptySlots }).map((_, id) => (
                    <div key={`empty-${id}`} className="dv-card dv-card-placeholder" style={{ display: "flex", visibility: 'hidden' }} />
                ))}
            </div>
        </motion.div>
    )
}